import { useState, useRef, useEffect, forwardRef, useImperativeHandle } from 'react'
import { colorFor } from '../data/userColors.js'

const TEAM = [
  'Ricky', 'Zoe', 'Christina', 'Katie', 'Margo', 'Emily',
  'Sandra', 'Sergina', 'Jonathan', 'Chris', 'Harold',
  'Jessie', 'Christine', 'Braden', 'Colin', 'Jane',
]

/**
 * MentionTextarea — a textarea that shows a team-member picker
 * whenever the user types `@` followed by letters.
 *
 * Behavior:
 *   - Type `@` → picker opens below (or above) the textarea
 *   - Keep typing → filter narrows to names starting with your query
 *   - ↑/↓ → navigate the picker
 *   - Enter or Tab → insert the selected name
 *   - Esc → close picker (keep typing regular text)
 *   - Click a name → insert it
 *
 * Drop-in replacement for a plain <textarea>. Same props:
 *   value, onChange, onKeyDown, placeholder, style, autoFocus
 */
const MentionTextarea = forwardRef(function MentionTextarea({
  value,
  onChange,
  onKeyDown,
  placeholder,
  style,
  autoFocus,
  pickerDirection = 'above', // 'above' | 'below'
  ...rest
}, ref) {
  const textareaRef = useRef(null)
  const [mention, setMention] = useState(null) // { query, start } | null
  const [selectedIdx, setSelectedIdx] = useState(0)

  // Expose the underlying <textarea> element via the forwarded ref so
  // callers (e.g., EmojiPickerButton) can read selectionStart and focus().
  useImperativeHandle(ref, () => textareaRef.current, [])

  const filtered = mention
    ? TEAM.filter((n) =>
        n.toLowerCase().startsWith(mention.query.toLowerCase())
      ).slice(0, 8)
    : []

  // Reset selection when the filter changes
  useEffect(() => {
    setSelectedIdx(0)
  }, [mention?.query])

  const detectMention = (text, cursor) => {
    const beforeCursor = text.slice(0, cursor)
    const atIdx = beforeCursor.lastIndexOf('@')

    if (atIdx === -1) return null

    const afterAt = beforeCursor.slice(atIdx + 1)

    // Bail if there's whitespace between @ and cursor, or query too long
    if (/[\s]/.test(afterAt) || afterAt.length > 20) return null

    // Require whitespace or start-of-string before @
    const charBefore = atIdx === 0 ? ' ' : text[atIdx - 1]
    if (!/[\s\n]/.test(charBefore) && atIdx !== 0) return null

    return { query: afterAt, start: atIdx }
  }

  const handleChange = (e) => {
    const newValue = e.target.value
    const cursor = e.target.selectionStart
    setMention(detectMention(newValue, cursor))
    onChange(e)
  }

  const selectMention = (name) => {
    if (!mention) return
    const before = value.slice(0, mention.start)
    const after = value.slice(mention.start + 1 + mention.query.length)
    const newValue = before + '@' + name + ' ' + after
    const newCursor = before.length + 1 + name.length + 1

    // Synthesize an event-like object for onChange
    onChange({ target: { value: newValue } })
    setMention(null)

    // Restore focus and set cursor just after the inserted mention
    setTimeout(() => {
      const el = textareaRef.current
      if (el) {
        el.focus()
        el.setSelectionRange(newCursor, newCursor)
      }
    }, 0)
  }

  const handleKeyDown = (e) => {
    if (mention && filtered.length > 0) {
      if (e.key === 'ArrowDown') {
        e.preventDefault()
        setSelectedIdx((i) => (i + 1) % filtered.length)
        return
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault()
        setSelectedIdx(
          (i) => (i - 1 + filtered.length) % filtered.length
        )
        return
      }
      if (e.key === 'Enter' || e.key === 'Tab') {
        e.preventDefault()
        selectMention(filtered[selectedIdx])
        return
      }
      if (e.key === 'Escape') {
        e.preventDefault()
        setMention(null)
        return
      }
    }
    onKeyDown?.(e)
  }

  const handleBlur = () => {
    // Close picker on blur, but on a slight delay so a click on the picker
    // fires first
    setTimeout(() => setMention(null), 150)
  }

  return (
    <div style={{ position: 'relative', width: '100%' }}>
      <textarea
        ref={textareaRef}
        value={value}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        onBlur={handleBlur}
        placeholder={placeholder}
        autoFocus={autoFocus}
        style={style}
        {...rest}
      />
      {mention && filtered.length > 0 && (
        <div
          style={{
            position: 'absolute',
            left: 0,
            right: 0,
            maxWidth: 240,
            ...(pickerDirection === 'above'
              ? { bottom: 'calc(100% + 4px)' }
              : { top: 'calc(100% + 4px)' }),
            background: '#fff',
            border: '1px solid var(--border)',
            borderRadius: 8,
            boxShadow: '0 6px 20px rgba(0,0,0,0.1)',
            padding: 4,
            zIndex: 1100,
            maxHeight: 240,
            overflowY: 'auto',
          }}
        >
          <div
            style={{
              fontSize: 9,
              fontWeight: 700,
              color: 'var(--muted)',
              textTransform: 'uppercase',
              letterSpacing: 0.06,
              padding: '4px 8px 2px',
            }}
          >
            Mention a teammate
          </div>
          {filtered.map((name, i) => (
            <button
              key={name}
              type="button"
              onMouseDown={(e) => {
                // onMouseDown fires before onBlur, so picker stays open
                e.preventDefault()
                selectMention(name)
              }}
              onMouseEnter={() => setSelectedIdx(i)}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                width: '100%',
                border: 'none',
                background: i === selectedIdx ? '#eff6ff' : 'transparent',
                padding: '6px 8px',
                borderRadius: 6,
                fontSize: 12,
                fontWeight: 500,
                color: 'var(--text)',
                cursor: 'pointer',
                textAlign: 'left',
              }}
            >
              <span
                style={{
                  display: 'inline-block',
                  width: 20,
                  height: 20,
                  borderRadius: '50%',
                  background: colorFor(name),
                  color: '#fff',
                  fontSize: 10,
                  fontWeight: 800,
                  lineHeight: '20px',
                  textAlign: 'center',
                }}
              >
                {name[0]}
              </span>
              <span>{name}</span>
              {i === selectedIdx && (
                <span
                  style={{
                    marginLeft: 'auto',
                    fontSize: 9,
                    color: 'var(--muted-light)',
                    fontWeight: 600,
                  }}
                >
                  ↵
                </span>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  )
})

export default MentionTextarea

